import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Globe2, MapPin, Plus, Settings2 } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../api/client";
import type { ListResponse, Location } from "../../api/types";
import { useVenueScope } from "../../app/venueScope";
import { useWorkspaceLens, WorkspacePointer } from "../../app/workspaceLens";
import { titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  DataTable,
  EmptyState,
  ErrorPanel,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { SkeletonTable, VenuesSkeleton } from "../../ui/skeletons";
import { useVenues } from "./queries";

interface PublicConfiguration {
  id: string;
  locationId: string;
  locationName: string;
  venueId: string;
  venueName: string;
  mode: string;
  details: {
    headline: string;
    description: string;
    requirePayment: boolean;
    depositBasisPoints: number;
  };
  publishedAt?: string;
  retiredAt?: string;
}

// A note kept on a space, as the editor stores it: an object holding one notes string.
function noteOf(value: unknown): string {
  if (!value || typeof value !== "object") return "";
  const item = (value as Record<string, unknown>).notes;
  return typeof item === "string" ? item.trim() : "";
}

// The time the calendar keeps free around anything scheduled in a space, as the events side
// reads it: setup before, teardown after.
function changeover(location: Location): string {
  if (!location.bufferBeforeMinutes && !location.bufferAfterMinutes) return "None";
  return `${location.bufferBeforeMinutes} min before · ${location.bufferAfterMinutes} min after`;
}

// How long a hire of the space may run, as the booking side reads it.
function hireLength(location: Location): string {
  const minimum = `${location.minimumMinutes} min`;
  return location.maximumMinutes
    ? `${minimum} to ${location.maximumMinutes} min`
    : `${minimum} or longer`;
}

export function VenuesPage() {
  const venues = useVenues();
  const lens = useWorkspaceLens();
  // The page shows the venue selected in the top bar. Switching venues happens there,
  // so this page carries no list of its own; adding a venue stays possible from here.
  const { selectedVenueId } = useVenueScope();
  const selected = venues.data?.items.find((venue) => venue.id === selectedVenueId) ?? null;
  const locations = useQuery({
    queryKey: ["venues", selected?.id, "locations"],
    queryFn: () => api<ListResponse<Location>>(`/api/v1/venues/${selected?.id}/locations`),
    enabled: Boolean(selected),
  });
  // The online catalog is lettings work, so it is only asked for on that side of the building,
  // and only for somebody the server would answer.
  const showCatalog = lens.isBookings && lens.allowsIn("bookings", "venues.manage");
  const configurations = useQuery({
    queryKey: ["public-booking", "configurations"],
    queryFn: () => api<ListResponse<PublicConfiguration>>("/api/v1/public-booking/configurations"),
    enabled: showCatalog,
  });

  if (venues.isLoading) return <VenuesSkeleton />;
  if (venues.error) return <ErrorPanel error={venues.error} />;

  const locationItems = locations.data?.items ?? [];
  const offerings = (configurations.data?.items ?? []).filter(
    (item) => item.venueId === selected?.id,
  );
  // One venue, one set of spaces, two ways of looking at them. The events side sees the rooms
  // its programme runs in and the time the calendar keeps between one thing and the next; the
  // booking side sees the spaces it lets out, how long a hire may run, and what is offered online.
  const description = lens.isEvents
    ? "Set up the venue you are viewing for your own programme: its details, the rooms and spaces it offers, and when each of them can be used."
    : "Set up the venue you are viewing for hire: its details, the spaces customers can book, their prices, and what is offered online.";
  const policiesLabel = lens.isEvents ? "Hours and closures" : "Hours and prices";
  return (
    <>
      <PageHeader
        title="Venues and spaces"
        description={description}
        actions={
          <Link className="button primary" to="/venues/new">
            <Plus size={17} /> Add venue
          </Link>
        }
      />
      {selected ? (
        <>
          <Card>
            <CardHeader
              title={selected.name}
              description={selected.description || "No description"}
              actions={
                <Link className="button secondary small" to={`/venues/${selected.id}/edit`}>
                  Edit venue
                </Link>
              }
            />
            <dl className="definition-list">
              <dt>Code</dt>
              <dd>{selected.code}</dd>
              <dt>Timezone</dt>
              <dd>{selected.timezone}</dd>
              <dt>Status</dt>
              <dd>
                <StatusPill value={selected.status} />
              </dd>
              <dt>Address</dt>
              <dd>
                {Object.values(selected.address ?? {})
                  .filter(Boolean)
                  .join(", ") || "Not set"}
              </dd>
            </dl>
          </Card>
          <Card>
            <CardHeader
              title={lens.isEvents ? "Rooms and spaces" : "Spaces for hire"}
              description={
                lens.isEvents
                  ? "Where your programme happens, with the changeover time the calendar keeps between one thing and the next."
                  : "Set booking lengths and preparation time so the calendar can prevent accidental clashes."
              }
              actions={
                <Link className="button primary small" to={`/venues/${selected.id}/locations/new`}>
                  <Plus size={16} /> Add space
                </Link>
              }
            />
            {locations.isLoading ? (
              <SkeletonTable bare columns={7} rows={4} />
            ) : locations.error ? (
              <ErrorPanel error={locations.error} />
            ) : locationItems.length ? (
              <DataTable
                caption={`Spaces at ${selected.name}`}
                items={locationItems}
                getKey={(location) => location.id}
                columns={[
                  {
                    label: "Space",
                    render: (location) => (
                      <span className="inline">
                        <MapPin size={16} />
                        <span>
                          <strong>{location.name}</strong>
                          <br />
                          <small>{location.code}</small>
                        </span>
                      </span>
                    ),
                  },
                  { label: "Type", render: (location) => titleCase(location.kind) },
                  {
                    label: "Capacity",
                    render: (location) => location.capacity ?? "Not available",
                  },
                  ...(lens.isEvents
                    ? [
                        {
                          label: "Changeover",
                          render: (location: Location) => changeover(location),
                        },
                        {
                          label: "Facilities",
                          render: (location: Location) =>
                            noteOf(location.facilities) || "Not recorded",
                        },
                      ]
                    : [
                        {
                          label: "Booking length",
                          render: (location: Location) => hireLength(location),
                        },
                        {
                          label: "Public",
                          render: (location: Location) =>
                            location.publicBookingEnabled ? "Eligible" : "Staff only",
                        },
                      ]),
                  {
                    label: "Status",
                    render: (location) => <StatusPill value={location.status} />,
                  },
                  {
                    label: "Actions",
                    render: (location) => (
                      <div className="inline">
                        <Link
                          className="button ghost small"
                          to={`/venues/${selected.id}/locations/${location.id}/edit`}
                        >
                          Edit
                        </Link>
                        <Link
                          className="button secondary small"
                          to={`/venues/${selected.id}/locations/${location.id}/policies`}
                        >
                          {lens.isEvents ? <CalendarClock size={15} /> : <Settings2 size={15} />}{" "}
                          {policiesLabel}
                        </Link>
                      </div>
                    ),
                  },
                ]}
              />
            ) : (
              <EmptyState
                title="No spaces yet"
                description={
                  lens.isEvents
                    ? "Add the first room, hall, field, or other area your programme uses."
                    : "Add the first room, hall, field, or other area customers can hire."
                }
              />
            )}
          </Card>
          {lens.isEvents ? (
            <WorkspacePointer target="bookings">
              Booking lengths, prices, and the online booking catalog for these spaces are set in
              the Booking workspace.
            </WorkspacePointer>
          ) : null}
          {showCatalog ? (
            <Card>
              <CardHeader
                title="Online booking catalog"
                description="These spaces appear on your online booking page. Times are checked against opening hours and existing bookings before a customer can choose them."
                actions={
                  <Link className="button secondary" to={`/venues/${selected.id}/offerings/new`}>
                    <Globe2 size={17} /> Publish offering
                  </Link>
                }
              />
              {configurations.isLoading ? (
                <SkeletonTable bare columns={4} rows={3} />
              ) : configurations.error ? (
                <ErrorPanel error={configurations.error} />
              ) : offerings.length ? (
                <DataTable
                  caption="Public booking offerings"
                  items={offerings}
                  getKey={(item) => item.id}
                  columns={[
                    {
                      label: "Offering",
                      render: (item) => (
                        <>
                          <strong>{item.details.headline}</strong>
                          <br />
                          <small>
                            {item.venueName} · {item.locationName}
                          </small>
                        </>
                      ),
                    },
                    { label: "Mode", render: (item) => <StatusPill value={item.mode} /> },
                    {
                      label: "Payment",
                      render: (item) => (item.details.requirePayment ? "Required" : "Not required"),
                    },
                    {
                      label: "Published",
                      render: (item) => (item.publishedAt && !item.retiredAt ? "Yes" : "No"),
                    },
                  ]}
                />
              ) : (
                <EmptyState
                  title="Nothing published"
                  description="Choose a space, add its availability and price, then publish it for online booking."
                />
              )}
            </Card>
          ) : null}
        </>
      ) : (
        <EmptyState
          title="No venues yet"
          description="Add the first property, then define the spaces inside it."
          action={
            <Link className="button primary" to="/venues/new">
              <Plus size={17} /> Add venue
            </Link>
          }
        />
      )}
    </>
  );
}
